import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import Clock from 'react-live-clock';
import { useTranslation } from 'react-i18next';
import logo from '../assets/Navbar/New logo.png';
import { FaRegEnvelope, FaWhatsapp, FaInstagram } from 'react-icons/fa';
import { LuPhone } from 'react-icons/lu';
import { SlLocationPin } from 'react-icons/sl';
import { IoLogoFacebook } from 'react-icons/io5';
import { GiHamburgerMenu } from 'react-icons/gi';
import { FaTiktok } from 'react-icons/fa';

// Define keyframes for slide-in animation
const slideIn = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleLanguageChange = (event) => {
        const selectedLanguage = event.target.value;
        i18n.changeLanguage(selectedLanguage);
    };

    return (
        <div>
            {/* Inject keyframes for animation */}
            <style>{slideIn}</style>

            {/* Small Screen Section (sm, <768px) */}
            <div className="block md:hidden bg-[#F3ECDE] border-b-[5px] border-gray-500 relative ">
                {/* Language Translator Button (Top Left) */}
                <div className="absolute top-4 left-4">
                    <select
                        onChange={handleLanguageChange}
                        className="border border-[#12392B] rounded-[10px] bg-white text-[#12392B]  px-2 py-1 cursor-pointer"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                    >
                        <option value="en" style={{ fontSize: "18px" }}>English</option>
                        <option value="de" style={{ fontSize: "18px" }}>German</option>
                        <option value="ru" style={{ fontSize: "18px" }}>Russian</option>
                        <option value="fr" style={{ fontSize: "18px" }}>French</option>
                        <option value="zh" style={{ fontSize: "18px" }}>Chinese</option>
                        <option value="ja" style={{ fontSize: "18px" }}>Japanese</option>
                    </select>
                </div>

                {/* Centered Logo */}
                <div className="flex justify-center py-4">
                    <img src={logo} alt="Cinnamon Mantra" className="h-40 sm:h-40" />
                </div>

                {/* Clock and Sri Lanka/Colombo (Top Right) */}
                <div className="absolute top-4 right-4 flex flex-col items-end gap-1">
                    <div className="bg-white px-2 py-1 rounded text-[#12392B] text-sm" style={{ fontFamily: 'Lato, sans-serif' }}>
                        <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Colombo'} />
                    </div>
                    <h3 className="text-sm text-[#12392B]">Sri Lanka/Colombo</h3>
                </div>

                {/* Menu Button (Bottom Right) */}
                <div className="absolute bottom-4 right-4">
                    <button
                        onClick={toggleMenu}
                        aria-label="Toggle mobile menu"
                        className="bg-transparent border-none"
                    >
                        <GiHamburgerMenu size={30} color="#12392B" />
                    </button>
                </div>
            </div>

            {/* Desktop Section (md and larger, ≥768px) */}
            <div className="hidden md:block">
                {/* Top Section */}
                <div
                    className={`flex items-center justify-between text-black ${isScrolled ? 'shadow-sm' : ''}`}
                    style={{
                        backgroundColor: '#F3ECDE',
                        height: '230px',
                        borderBottom: '5px solid #808080',
                        padding: '0 20px',
                        position: 'relative',
                    }}
                >
                    {/* Centered Logo */}
                    <div
                        className="flex justify-center w-full mt-2"
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <img src={logo} alt="Cinnamon Mantra" style={{ height: '220px' }} />
                    </div>

                    {/* Social Icons and Clock in Top Right Corner */}
                    <div className="flex flex-col items-end gap-2" style={{ position: 'absolute', top: '50px', right: '20px' }}>
                        <div
                            className="bg-white px-3 py-1 rounded"
                            style={{ fontFamily: 'Lato, sans-serif', fontSize: '20px', color: '#12392B', width: 'auto', height: '40px' }}
                        >
                            <Clock format={'HH:mm:ss'} ticking={true} timezone={'Asia/Colombo'} />
                        </div>
                        <h3>Sri Lanka/Colombo</h3>
                        <div className="flex gap-2 mt-16">
                            <a
                                href="tel:+94775340670"
                                className="text-black"
                                aria-label="Call us"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#5c4033';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#000';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <LuPhone size={25} color="#12392B" />
                            </a>
                            <a
                                href="https://maps.app.goo.gl/UWGUkv9DGxMf9kYy8"
                                className="text-black"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View location"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#5c4033';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#000';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <SlLocationPin size={25} color="#12392B" />
                            </a>
                            <a
                                href="mailto:sunflower.beruwala@gmail.com"
                                className="text-black"
                                aria-label="Email us"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#5c4033';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#000';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaRegEnvelope size={25} color="#12392B" />
                            </a>
                            <a
                                href="https://web.facebook.com/"
                                className="text-black"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visit our Facebook page"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#5c4033';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#000';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <IoLogoFacebook size={25} color="#12392B" />
                            </a>
                            <a
                                href="https://wa.me/+94775340670"
                                className="text-black"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Contact us on WhatsApp"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#5c4033';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#000';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaWhatsapp size={25} color="#12392B" />
                            </a>
                            <a
                                href="https://www.instagram.com/"
                                className="text-black"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visit our Instagram page"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#5c4033';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#000';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaInstagram size={25} color="#12392B" />
                            </a>
                            <a
                                href="https://www.tiktok.com/"
                                className="text-black"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Visit our TikTok page"
                                style={{
                                    transition: 'color 0.3s, transform 0.3s',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#5c4033';
                                    e.target.style.transform = 'scale(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#000';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                <FaTiktok size={25} color="#12392B" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Bottom Section (Desktop Navigation) */}
                <div
                    className="flex justify-between items-center px-4 py-2"
                    style={{
                        backgroundColor: '#E6A71A',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        boxShadow: isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                    }}
                >
                    {/* Centered Navigation Links */}
                    <div className="flex justify-center flex-grow-1">
                        <ul className="flex mb-0" style={{ fontFamily: 'Copperplate, serif', fontSize: '25px', marginLeft: '100px' }}>
                            <li className="nav-item">
                                <NavLink
                                    className="text-black px-4 py-2"
                                    to="/"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                    onMouseEnter={(e) => (e.target.style.color = '#5c4033')}
                                    onMouseLeave={(e) => (e.target.style.color = '#000')}
                                >
                                    {t('navbar.home')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-black px-4 py-2"
                                    to="/about-us"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                    onMouseEnter={(e) => (e.target.style.color = '#5c4033')}
                                    onMouseLeave={(e) => (e.target.style.color = '#000')}
                                >
                                    {t('navbar.aboutUs')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-black px-4 py-2"
                                    to="/villa"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                    onMouseEnter={(e) => (e.target.style.color = '#5c4033')}
                                    onMouseLeave={(e) => (e.target.style.color = '#000')}
                                >
                                    {t('navbar.resort')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-black px-4 py-2"
                                    to="/ayurveda-spa"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                    onMouseEnter={(e) => (e.target.style.color = '#5c4033')}
                                    onMouseLeave={(e) => (e.target.style.color = '#000')}
                                >
                                    {t('navbar.ayurvedaWellness')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-black px-4 py-2"
                                    to="/packages"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                    onMouseEnter={(e) => (e.target.style.color = '#5c4033')}
                                    onMouseLeave={(e) => (e.target.style.color = '#000')}
                                >
                                    {t('navbar.packages')}
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink
                                    className="text-black px-4 py-2"
                                    to="/contact-us"
                                    style={({ isActive }) => ({
                                        textDecoration: isActive ? 'underline' : 'none',
                                        transition: 'color 0.3s',
                                    })}
                                    onMouseEnter={(e) => (e.target.style.color = '#5c4033')}
                                    onMouseLeave={(e) => (e.target.style.color = '#000')}
                                >
                                    {t('navbar.contactUs')}
                                </NavLink>
                            </li>
                        </ul>
                    </div>

                    {/* Language Translator Button */}
                    <div style={{ marginRight: '20px' }}>
                        <select
                            onChange={handleLanguageChange}
                            className="border border-[#12392B] rounded-[5px] bg-white text-[#12392B] text-base px-3 py-1 cursor-pointer"
                            style={{
                                fontFamily: 'Lato, sans-serif',
                                fontSize: '16px',
                                padding: '5px 10px',
                            }}
                        >
                            <option value="en">English</option>
                            <option value="de">German</option>
                            <option value="ru">Russian</option>
                            <option value="fr">French</option>
                            <option value="zh">Chinese</option>
                            <option value="ja">Japanese</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div
                    className="block md:hidden fixed top-0 right-0 h-screen w-4/5 max-w-[300px] bg-white shadow-[-4px_0_10px_rgba(0,0,0,0.2)] border-l border-gray-200 z-[1001] p-5"
                    style={{ animation: 'slideIn 0.3s ease-in-out', fontFamily: 'Lato, sans-serif' }}
                >
                    {/* Close Button */}
                    <button
                        onClick={toggleMenu}
                        aria-label="Close mobile menu"
                        className="absolute top-[50px] right-5 bg-[#f4e4bc] text-[#5c4033] border border-[#5c4033] rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold hover:bg-[#d9c8a0] hover:scale-110 transition-all"
                    >
                        X
                    </button>

                    {/* Navigation Links */}
                    <ul className="flex flex-col mt-[80px]">
                        <li>
                            <NavLink
                                to="/"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#f4e4bc] py-3 px-5 text-[1.5rem]"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    fontFamily: 'Copperplate, serif',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('navbar.home')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/about-us"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#f4e4bc] py-3 px-5 text-[1.5rem]"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    fontFamily: 'Copperplate, serif',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('navbar.aboutUs')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/villa"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#f4e4bc] py-3 px-5 text-[1.5rem]"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    fontFamily: 'Copperplate, serif',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('navbar.resort')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/ayurveda-spa"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#f4e4bc] py-3 px-5 text-[1.5rem]"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    fontFamily: 'Copperplate, serif',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('navbar.ayurvedaWellness')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/packages"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#f4e4bc] py-3 px-5 text-[1.5rem]"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    fontFamily: 'Copperplate, serif',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('navbar.packages')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/contact-us"
                                onClick={toggleMenu}
                                className="block text-black hover:bg-[#f4e4bc] py-3 px-5 text-[1.5rem]"
                                style={({ isActive }) => ({
                                    textDecoration: isActive ? 'underline' : 'none',
                                    fontFamily: 'Copperplate, serif',
                                    transition: 'color 0.3s, background-color 0.3s',
                                })}
                            >
                                {t('navbar.contactUs')}
                            </NavLink>
                        </li>
                    </ul>

                    {/* Social Icons */}
                    <div className="flex justify-center gap-3 mt-4 pt-4 border-t border-gray-200">
                        <a
                            href="tel:+94775340670"
                            aria-label="Call us"
                            className="text-black"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#5c4033';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#000';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <LuPhone size={24} color="#12392B" />
                        </a>
                        <a
                            href="https://maps.app.goo.gl/UWGUkv9DGxMf9kYy8"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View location"
                            className="text-black"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#5c4033';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#000';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <SlLocationPin size={24} color="#12392B" />
                        </a>
                        <a
                            href="mailto:sunflower.beruwala@gmail.com"
                            aria-label="Email us"
                            className="text-black"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#5c4033';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#000';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <FaRegEnvelope size={24} color="#12392B" />
                        </a>
                        <a
                            href="https://web.facebook.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our Facebook page"
                            className="text-black"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#5c4033';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#000';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <IoLogoFacebook size={24} color="#12392B" />
                        </a>
                        <a
                            href="https://wa.me/+94775340670"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Contact us on WhatsApp"
                            className="text-black"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#5c4033';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#000';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <FaWhatsapp size={24} color="#12392B" />
                        </a>
                        <a
                            href="https://www.instagram.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our Instagram page"
                            className="text-black"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#5c4033';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#000';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <FaInstagram size={24} color="#12392B" />
                        </a>
                        <a
                            href="https://www.tiktok.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our TikTok page"
                            className="text-black"
                            style={{
                                transition: 'color 0.3s, transform 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = '#5c4033';
                                e.target.style.transform = 'scale(1.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#000';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <FaTiktok size={24} color="#12392B" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navbar;